import { desanitizeKey } from '@/helper/utils';
import { X } from 'lucide-react';
import React from 'react';

interface DetailField {
  label: string;
  value: string | string[] | number | number[];
  list?: boolean;
  fullWidth?: boolean;
}

export const DetailRow: React.FC<DetailField> = ({ label, value, list, fullWidth }) => {
  let spanClasses = '';

  if (label === 'Status') {
    const statusBgMap: Record<string, string> = {
      draft: 'bg-[#E24A4A]/80 text-[#1A0206]',
      scheduled: 'bg-[#4C86E7]/80 text-[#1A0206]',
      failed: 'bg-[#F4A12C]/80 text-[#1A0206]',
      sent: 'bg-[#0BA96F]/80 text-[#1A0206]',
      active: 'bg-[#0BA96FCC] text-[#1A0206] px-3 py-1 rounded-full text-xs font-medium',
      inactive: 'bg-[#E24A4ACC] text-[#1A0206] px-3 py-1 rounded-full text-xs font-medium',
    };

    spanClasses = statusBgMap[(value as string).toLowerCase()] || 'bg-gray-300 text-green-700';

    if (!spanClasses.includes('inline-block')) {
      spanClasses +=
        ' inline-block px-3 py-1 text-center rounded-full text-xs font-Regular min-w-[100px]';
    }
  } else if (label === 'ODK File Name') {
    spanClasses += 'bg-[#C9C9C9CC] text-[#1A0206] px-3 py-1 rounded-full text-xs font-medium';
  } else {
    spanClasses += 'bg-[#C9C9C9CC] text-[#1A0206] px-3 rounded-xl text-sm';
  }

  return (
    <div className="font-medium">
      <label className="block text-sm text-[#FFF8ED] mb-1">{label}</label>
      <div
        className={`bg-[#FFF8ED33] text-[#FFF8ED] px-3 py-2 rounded-lg text-sm ${
          Array.isArray(value) ? 'flex flex-wrap gap-2' : ''
        }`}
      >
        {label === 'Status' ? (
          <span className={spanClasses}>{desanitizeKey(value as string)}</span>
        ) : Array.isArray(value) ? (
          value.map((item) => (
            <span key={item} className={spanClasses}>
              {desanitizeKey(item as string)}
            </span>
          ))
        ) : list ? (
          <div className="flex justify-start">
            <span className={spanClasses}>{value as string}</span>
          </div>
        ) : typeof value === 'string' ? (
          label === 'Currency Type' || label === 'Reward Amount' ? (
            value.toUpperCase()
          ) : (
            value
          )
        ) : (
          value
        )}
      </div>
    </div>
  );
};

interface ViewDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: Record<string, any>;
  fields: DetailField[];
}

const ViewDetailsModal: React.FC<ViewDetailsModalProps> = ({
  isOpen,
  onClose,
  title,
  data,
  fields,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed left-[255px] top-[75px] right-0 bottom-0 z-[9999] flex items-center justify-center"
      style={{
        background:
          'linear-gradient(174.07deg, rgba(173, 12, 98, 0.85) 0.38%, rgba(98, 28, 67, 0.85) 95.87%)',
      }}
    >
      <div className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-[#621C43] rounded-lg shadow-lg">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#E0C9D8] flex-shrink-0">
          <h2 className="text-xl font-Lato text-[#FFF8ED] font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-[#FFF8ED] hover:text-gray-300 transition-colors"
          >
            <X height={18} width={18} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-4 font-Lato font-medium">
            {fields.map((field) => (
              <div key={field.label} className={field.fullWidth ? 'col-span-2' : ''}>
                <DetailRow
                  key={field.label}
                  label={field.label}
                  value={field.value}
                  list={field.list}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDetailsModal;
